/**
 * Thailand Digital Arrival Card (TDAC) reminders.
 * Official FAQ describes the window both as including arrival day and as up to 72 hours.
 * Do not encode an untested calendar interpretation. For a 23 Oct Thailand arrival,
 * a 21 Oct Europe/London morning reminder is inside both descriptions.
 * https://tdac.immigration.go.th/manual/en/faq.html
 */

import { DateTime } from "luxon";
import type { TripDocument } from "./schema.ts";
import type { TripTask } from "./push.ts";

export const TDAC_OFFICIAL_URL = "https://tdac.immigration.go.th/";
export const TDAC_TASK_ID = "tdac-arrival";

export function thailandArrivalFlight(doc: TripDocument) {
  const inbound = [...doc.flights]
    .filter((flight) => {
      const dest = flight.arrival.airport.toUpperCase();
      return dest === "CNX" || dest === "BKK" || dest === "HKT" || dest === "DMK";
    })
    .sort((a, b) => a.arrival.instant.localeCompare(b.arrival.instant));
  return inbound[0] ?? null;
}

export function tdacReminderInstant(arrivalLocalDate: string, homeZone = "Europe/London"): DateTime {
  const twoDaysBefore = DateTime.fromISO(arrivalLocalDate, { zone: homeZone }).minus({ days: 2 }).set({
    hour: 8,
    minute: 0,
    second: 0,
    millisecond: 0,
  });
  return twoDaysBefore;
}

export function expectedTdacTravellerKeys(doc: TripDocument): string[] {
  const keys: string[] = [];
  for (const household of doc.households) {
    const people = household.adults + household.children;
    for (let i = 1; i <= people; i += 1) keys.push(`${household.id}:${i}`);
  }
  if (keys.length === 0) {
    for (let i = 1; i <= doc.trip.travellerCount; i += 1) keys.push(`trip:${i}`);
  }
  return keys;
}

export function tdacTaskFromTrip(doc: TripDocument, existing?: TripTask | null): TripTask {
  const arrival = thailandArrivalFlight(doc);
  const arrivalDate = arrival?.arrival.local.slice(0, 10) ?? null;
  const reminder = arrivalDate ? tdacReminderInstant(arrivalDate, doc.trip.homeTimezone) : null;
  const expected = expectedTdacTravellerKeys(doc);
  const coversKey = arrival ? `${arrival.number}:${arrivalDate}` : null;
  const detailsChanged = Boolean(existing?.coversKey && coversKey && existing.coversKey !== coversKey);
  const completedAt = detailsChanged ? null : existing?.completedAt ?? null;
  const completedTravellerKeys = detailsChanged ? [] : existing?.completedTravellerKeys.filter((key) => expected.includes(key)) ?? [];
  return {
    id: TDAC_TASK_ID,
    kind: "tdac",
    title: "Thailand Digital Arrival Card",
    responsibleProfileId: "chris",
    householdId: null,
    officialUrl: TDAC_OFFICIAL_URL,
    dueDate: reminder?.toISODate() ?? null,
    dueZone: doc.trip.homeTimezone,
    completedTravellerKeys,
    expectedTravellerKeys: expected,
    confirmationAttached: detailsChanged ? false : existing?.confirmationAttached ?? false,
    completedAt,
    coversKey,
    notes: detailsChanged
      ? "Arrival details changed — review submission. Opening the official site is not completion."
      : arrivalDate
        ? `Submit on the official portal within the published window before arriving in Thailand on ${arrivalDate}. Opening the website is not completion.`
        : "Thailand arrival date is not yet on a flight record.",
  };
}

export function tdacIsComplete(task: TripTask): boolean {
  if (task.completedAt) return true;
  if (!task.expectedTravellerKeys.length) return false;
  return task.expectedTravellerKeys.every((key) => task.completedTravellerKeys.includes(key));
}

export function tdacRemaining(task: TripTask): number {
  return task.expectedTravellerKeys.filter((key) => !task.completedTravellerKeys.includes(key)).length;
}
